/**
 * Backward-compatible helper to get all initiator pedal IDs from a trade document.
 * Old trades store `initiatorPedalId` (string), new trades store `initiatorPedalIds` (array).
 *
 * NOTE: This file is intentionally mirrored in functions/tradeUtils.js (CommonJS).
 * Keep both copies in sync when making changes.
 */
export const getInitiatorPedalIds = (trade) => {
  if (trade.initiatorPedalIds && Array.isArray(trade.initiatorPedalIds) && trade.initiatorPedalIds.length > 0) {
    return trade.initiatorPedalIds;
  }
  if (trade.initiatorPedalId) {
    return [trade.initiatorPedalId];
  }
  return [];
};

export const MAX_INITIATOR_PEDALS = 4;

// ── Payout gating (client-only) ───────────────────────────────────────────
// Single source of truth for "is this user the cash receiver?", "how much cash
// do they NET after applying it to fees?", and "do they therefore need to set
// up a Stripe payout account?". Used by TradeConfirm (button label + onboarding
// redirect) AND TradeSettlement (settle gate) so the gross-vs-net decision can
// never diverge between the two pages. NOT mirrored to functions/ — purely UI.

/**
 * Whether `userId` is the party RECEIVING cash in this trade.
 * Mirrors createStripeCheckout.js's cash-direction logic.
 */
export const getUserReceivesCash = (trade, userId) => {
  const cash = Number(trade?.cashAmount) || 0;
  if (cash <= 0) return false;
  if (trade.cashFlow && trade.cashFlow.receiver) return trade.cashFlow.receiver === userId;
  const isFromUser = userId === trade.fromUserId;
  if (trade.type === 'ask') return isFromUser;
  if (trade.type === 'offer') return !isFromUser;
  return false;
};

/**
 * NET cash the receiver actually pockets after (optionally) applying their
 * received cash toward the fees they owe.
 * - applyCashCredit ON  → net = max(0, grossReceived - totalFees)
 * - applyCashCredit OFF → net = grossReceived (they pay fees separately)
 */
export const getNetCashReceived = ({ grossReceived, totalFees, applyCashCredit = true }) => {
  const gross = Number(grossReceived) || 0;
  if (gross <= 0) return 0;
  if (!applyCashCredit) return gross;
  const appliedToFees = Math.min(gross, Number(totalFees) || 0);
  return gross - appliedToFees;
};

/**
 * A payout account is only required when the user will actually net a payout
 * (> 0) AND hasn't completed Stripe onboarding yet.
 */
export const userNeedsPayoutOnboarding = ({ netCashReceived, stripeOnboardingComplete }) =>
  (Number(netCashReceived) || 0) > 0 && !stripeOnboardingComplete;
